import type { AssetStatus, CascadeState, JobStatus } from '@/types/nf'

export type StatusViewContext = 'project' | 'chapter' | 'job' | 'asset' | 'cascade' | 'quality'
export type StatusTone = 'success' | 'running' | 'warning' | 'danger' | 'partial' | 'muted' | 'unknown'

export interface StatusLabel {
  textKey: string
  textOptions?: Record<string, string>
  tone: StatusTone
  color: string
  pulsing?: boolean
}

const runningJobStatuses = new Set<JobStatus>([
  'pending',
  'structure_planning',
  'paragraph_generating',
  'qc_check',
  'copy_editing',
  'scene_editing',
  'retry_scheduled',
])

const knownStatus: Record<StatusViewContext, ReadonlySet<string>> = {
  project: new Set(['planning', 'writing', 'in_progress', 'paused', 'completed', 'archived']),
  chapter: new Set([
    'done',
    'completed',
    'complete',
    'finished',
    'stopped',
    'awaiting_confirm',
    'running',
    'generating',
    'streaming',
    'writing',
    'drafting',
    'needs_review',
    'partial_result',
    'failed',
  ]),
  job: new Set<JobStatus>([
    'pending',
    'structure_planning',
    'paragraph_generating',
    'qc_check',
    'copy_editing',
    'scene_editing',
    'retry_scheduled',
    'done',
    'failed',
    'needs_review',
    'partial_result',
  ]),
  asset: new Set<AssetStatus>(['pending', 'passed', 'needs_review', 'failed', 'isolated']),
  cascade: new Set<CascadeState>(['running', 'awaiting_confirm', 'stopped']),
  quality: new Set(['passed', 'done', 'failed', 'needs_review', 'partial_result', 'unknown']),
}

const statusTone: Record<StatusViewContext, Record<string, StatusTone>> = {
  project: {
    planning: 'warning',
    writing: 'running',
    in_progress: 'running',
    paused: 'muted',
    completed: 'success',
    archived: 'muted',
  },
  chapter: {
    done: 'success',
    completed: 'success',
    complete: 'success',
    finished: 'success',
    stopped: 'danger',
    awaiting_confirm: 'warning',
    running: 'running',
    generating: 'running',
    streaming: 'running',
    writing: 'running',
    drafting: 'running',
    needs_review: 'warning',
    partial_result: 'partial',
    failed: 'danger',
  },
  job: {
    pending: 'running',
    structure_planning: 'running',
    paragraph_generating: 'running',
    qc_check: 'running',
    copy_editing: 'running',
    scene_editing: 'running',
    retry_scheduled: 'running',
    done: 'success',
    failed: 'danger',
    needs_review: 'warning',
    partial_result: 'partial',
  },
  asset: {
    pending: 'running',
    passed: 'success',
    needs_review: 'warning',
    failed: 'danger',
    isolated: 'partial',
  },
  cascade: {
    running: 'running',
    awaiting_confirm: 'warning',
    stopped: 'danger',
  },
  quality: {
    passed: 'success',
    done: 'success',
    failed: 'danger',
    needs_review: 'warning',
    partial_result: 'partial',
    unknown: 'unknown',
  },
}

const toneColor = {
  success: 'var(--nf-st-done)',
  running: 'var(--nf-st-running)',
  warning: 'var(--nf-st-review)',
  danger: 'var(--nf-st-failed)',
  partial: 'var(--nf-st-partial)',
  muted: 'var(--muted-foreground)',
  unknown: 'var(--muted-foreground)',
} satisfies Record<StatusTone, string>

function normalizeStatus(status: string | null | undefined) {
  const normalized = status?.trim().toLowerCase()
  return normalized || null
}

function fallbackText(status: string) {
  return status.replaceAll('_', ' ')
}

export function isRunningStatus(status: string | null | undefined, context: StatusViewContext): boolean {
  const normalized = normalizeStatus(status)
  if (!normalized) return false
  if (context === 'job') return runningJobStatuses.has(normalized as JobStatus)
  return statusTone[context][normalized] === 'running'
}

export function statusLabel(status: string | null | undefined, context: StatusViewContext): StatusLabel {
  const normalized = normalizeStatus(status)
  if (!normalized) {
    return { textKey: 'status.common.unknown', tone: 'unknown', color: toneColor.unknown }
  }
  if (!knownStatus[context].has(normalized)) {
    return {
      textKey: 'status.common.custom',
      textOptions: { status: fallbackText(normalized) },
      tone: 'unknown',
      color: toneColor.unknown,
    }
  }
  const tone = statusTone[context][normalized]
  const label: StatusLabel = {
    textKey: `status.${context}.${normalized}`,
    tone,
    color: toneColor[tone],
  }
  if (context === 'job' || context === 'cascade') label.pulsing = isRunningStatus(normalized, context)
  return label
}
